import React, { useState, useEffect, useMemo, useRef } from 'react';
import { formatHandle } from '../utils/formatHandle';
import {
  Heart,
  Image,
  MapPin,
  MessageCircle,
  PlusCircle,
  Share2,
  Smile,
  Users,
  X
} from 'lucide-react';
import { Post, PostComment, UserProfile, subscribeToPosts, convertTimestampToDate, getUserProfileByUid, addComment, subscribeToCommentsForPost, togglePostLike, createPost, uploadImage } from '../firebase/firestore';
import { subscribeToFriends } from '../firebase/friends';
import { useAuth } from '../contexts/AuthContext';
import { User } from 'lucide-react';
import { Link } from 'react-router-dom';
import PostAuthorLink from '../components/PostAuthorLink';
import { getProfilePath } from '../utils/formatHandle';
import RightRail from '../components/shell/RightRail';

const PostCommentsSection: React.FC<{
  postId: string;
  currentUser: any;
  formatTimestamp: (timestamp: Date | any) => string;
  onNotification: (message: string, type: 'success' | 'error') => void;
}> = ({ postId, currentUser, formatTimestamp, onNotification }) => {
  const [comments, setComments] = useState<PostComment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const unsub = subscribeToCommentsForPost(postId, setComments);
    return unsub;
  }, [postId]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() || !currentUser) return;
    setSubmitting(true);
    try {
      const profile = await getUserProfileByUid(currentUser.uid);
      await addComment(postId, {
        authorId: currentUser.uid,
        authorName: profile?.displayName || currentUser.displayName || 'Anonymous',
        authorHandle: profile?.handle,
        authorImageUrl: profile?.profileImageUrl,
        content: commentText.trim()
      });
      setCommentText('');
      onNotification('Comment added', 'success');
    } catch (err) {
      console.error('Error adding comment:', err);
      onNotification('Failed to add comment', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-dark-border/70">
      <p className="text-sm font-semibold text-text-primary mb-2">Comments</p>
      {comments.length === 0 ? (
        <p className="text-sm text-text-muted mb-3">No comments yet. Be the first!</p>
      ) : (
        <ul className="space-y-3 mb-4">
          {comments.map((c) => (
            <li key={c.id} className="flex gap-2.5">
              {c.authorImageUrl ? (
                <img src={c.authorImageUrl} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-surface-sunken flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-text-muted" />
                </div>
              )}
              <div className="min-w-0 flex-1 rounded-2xl bg-surface-sunken/60 px-3 py-2">
                <p className="text-xs text-text-muted">
                  {c.authorName}
                  {c.authorHandle && <span className="ml-1">{formatHandle(c.authorHandle)}</span>}
                  {' · '}
                  {formatTimestamp(c.createdAt)}
                </p>
                <p className="text-sm text-text-primary whitespace-pre-wrap">{c.content}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
      {currentUser && (
        <form onSubmit={handleSubmitComment} className="flex flex-col sm:flex-row gap-2">
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Write a comment..."
            className="flex-1 bg-surface-sunken/70 border border-dark-border/70 rounded-2xl px-4 py-2.5 text-sm text-text-primary placeholder-text-muted min-h-[60px] focus:outline-none focus:border-accent-blue/60 transition-colors"
            rows={2}
          />
          <button
            type="submit"
            disabled={submitting || !commentText.trim()}
            className="self-end sm:self-auto rounded-full bg-accent-blue px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 ease-premium hover:bg-accent-strong disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Posting...' : 'Post'}
          </button>
        </form>
      )}
    </div>
  );
};

const categoryLabel: Record<string, string> = {
  cultural: 'Cultural',
  community: 'Community',
  learning: 'Learning',
  events: 'Events',
  marketplace: 'Marketplace',
  introduction: 'Introduction'
};

const categoryStyle: Record<string, string> = {
  cultural: 'bg-accent-purple/10 text-accent-purple',
  community: 'bg-accent-blue/10 text-accent-blue',
  learning: 'bg-accent-green/10 text-accent-green',
  events: 'bg-accent-orange/10 text-accent-orange',
  marketplace: 'bg-accent-orange/10 text-accent-orange',
  introduction: 'bg-accent-green/10 text-accent-green'
};

const quickPostActions = [
  { to: null, label: 'Photo/Video', icon: Image, color: 'text-accent-green' },
  { to: null, label: 'Feeling/Activity', icon: Smile, color: 'text-accent-orange' },
  { to: '/nearby', label: 'Check In', icon: MapPin, color: 'text-rose-500' }
];

type FeedTab = 'forYou' | 'following' | 'communities' | 'trending';

const FEED_TABS: Array<{ id: FeedTab; label: string }> = [
  { id: 'forYou', label: 'Global' },
  { id: 'following', label: 'Friends' },
  { id: 'communities', label: 'Communities' },
  { id: 'trending', label: 'Trending' }
];

const TAB_EMPTY_COPY: Record<FeedTab, string> = {
  forYou: 'No posts yet — be the first to share something.',
  following: 'Posts from your friends will show up here. Add a few friends to fill this tab.',
  communities: 'Community posts will appear here as spaces start sharing.',
  trending: 'Nothing trending yet — the most-loved posts will rise here.'
};

/** Renders a post's kinship-group chips (colors are the ceremony colors, not theme tokens). */
const KinshipGroupChips: React.FC<{ groups: string[] }> = ({ groups }) => (
  <div className="flex flex-wrap gap-1 mb-3">
    {groups.map((group: string) => (
      <span
        key={group}
        className="px-2.5 py-0.5 text-xs rounded-full border capitalize"
        style={{
          backgroundColor: group.toLowerCase().includes('yellow') ? '#fef3c7' : group.toLowerCase().includes('red') ? '#fee2e2' : group.toLowerCase().includes('black') ? '#1f2937' : group.toLowerCase().includes('white') ? '#ffffff' : '#e5e7eb',
          color: group.toLowerCase().includes('black') ? '#ffffff' : group.toLowerCase().includes('white') ? '#000000' : group.toLowerCase().includes('yellow') ? '#92400e' : group.toLowerCase().includes('red') ? '#991b1b' : '#374151',
          borderColor: group.toLowerCase().includes('white') ? '#d1d5db' : 'transparent'
        }}
      >
        {group}
      </span>
    ))}
  </div>
);

const Feed: React.FC = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<FeedTab>('forYou');
  const [friendIds, setFriendIds] = useState<Set<string>>(new Set());
  const [expandedCommentsPostId, setExpandedCommentsPostId] = useState<string | null>(null);
  const [selectedPostForFeed, setSelectedPostForFeed] = useState<Post | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showComposer, setShowComposer] = useState(false);
  const [composerData, setComposerData] = useState({ title: '', content: '', imageUrl: '' });
  const [composerCategory, setComposerCategory] = useState<Post['category']>('community');
  const [composerSubmitting, setComposerSubmitting] = useState(false);
  const [composerUploading, setComposerUploading] = useState(false);

  useEffect(() => {
    const unsub = subscribeToPosts((newPosts) => {
      setPosts(newPosts);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      return;
    }

    getUserProfileByUid(user.uid)
      .then(setUserProfile)
      .catch((error) => console.error('Error loading feed profile:', error));
  }, [user]);

  useEffect(() => {
    if (!user) {
      setFriendIds(new Set());
      return;
    }
    const unsub = subscribeToFriends(user.uid, (friends) =>
      setFriendIds(new Set(friends.map((friend) => friend.friendId)))
    );
    return () => unsub();
  }, [user]);

  // Members in their first two weeks get a gentler feed: a pinned welcome
  // card, and introduction posts surfaced first so their first impression is
  // people saying hello — not whatever happened to be posted last.
  const isNewMember = useMemo(() => {
    if (!user || !userProfile?.createdAt) return false;
    const created = convertTimestampToDate(userProfile.createdAt);
    return Date.now() - created.getTime() < 14 * 24 * 60 * 60 * 1000;
  }, [user, userProfile]);

  const [welcomeCardDismissed, setWelcomeCardDismissed] = useState(false);
  useEffect(() => {
    if (!user) return;
    setWelcomeCardDismissed(localStorage.getItem(`kw-welcome-card-dismissed-${user.uid}`) === '1');
  }, [user]);

  const dismissWelcomeCard = () => {
    setWelcomeCardDismissed(true);
    if (user) {
      try {
        localStorage.setItem(`kw-welcome-card-dismissed-${user.uid}`, '1');
      } catch {
        /* private mode */
      }
    }
  };

  const visiblePosts = useMemo(() => {
    const likeCount = (post: Post) =>
      (post.likedBy && post.likedBy.length > 0 ? post.likedBy.length : post.likes) || 0;
    switch (activeTab) {
      case 'following':
        return posts.filter((post) => friendIds.has(post.authorId));
      case 'communities':
        return posts.filter((post) => post.category === 'community' || post.category === 'introduction');
      case 'trending':
        return [...posts].sort((a, b) => likeCount(b) + b.comments - (likeCount(a) + a.comments));
      default: {
        if (!isNewMember) return posts;
        const introductions = posts.filter((post) => post.category === 'introduction');
        const rest = posts.filter((post) => post.category !== 'introduction');
        return [...introductions, ...rest];
      }
    }
  }, [posts, activeTab, friendIds, isNewMember]);

  // Windowed feed: render a page at a time and grow it as the reader nears
  // the bottom, so a large community doesn't mean a heavy first paint.
  const PAGE_SIZE = 8;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeTab]);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || visibleCount >= visiblePosts.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((count) => count + PAGE_SIZE);
        }
      },
      { rootMargin: '600px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [visibleCount, visiblePosts.length]);

  const renderedPosts = visiblePosts.slice(0, visibleCount);

  const handleShare = async (post: Post) => {
    const url = `${window.location.origin}/post/${post.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      showNotification('Post link copied to clipboard');
    } catch (error) {
      // Sharing cancelled or clipboard blocked — not worth an error toast
      if ((error as DOMException)?.name !== 'AbortError') {
        console.error('Error sharing post:', error);
      }
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  const closeComposer = () => {
    setShowComposer(false);
    setComposerData({ title: '', content: '', imageUrl: '' });
    setComposerCategory('community');
  };

  const handleComposerImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showNotification('Please select an image file', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotification('File size must be less than 5MB', 'error');
      return;
    }
    setComposerUploading(true);
    try {
      const imageUrl = await uploadImage(file, `post-images/${user?.uid}`);
      setComposerData((prev) => ({ ...prev, imageUrl }));
    } catch (error) {
      console.error('Error uploading image:', error);
      showNotification('Failed to upload image', 'error');
    } finally {
      setComposerUploading(false);
    }
  };

  const handleComposerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!composerData.title.trim() || !composerData.content.trim()) {
      showNotification('Title and content are required', 'error');
      return;
    }
    setComposerSubmitting(true);
    try {
      const profile = await getUserProfileByUid(user.uid);
      if (!profile) {
        showNotification('User profile not found', 'error');
        return;
      }
      await createPost({
        authorId: user.uid,
        authorName: profile.displayName,
        authorHandle: profile.handle,
        authorImageUrl: profile.profileImageUrl,
        title: composerData.title.trim(),
        content: composerData.content.trim(),
        category: composerCategory,
        images: composerData.imageUrl ? [composerData.imageUrl] : undefined
      });
      closeComposer();
      showNotification('Post created successfully!');
    } catch (error) {
      console.error('Error creating post:', error);
      showNotification('Failed to create post', 'error');
    } finally {
      setComposerSubmitting(false);
    }
  };

  const formatTimestamp = (timestamp: Date | any) => {
    const date = convertTimestampToDate(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleLike = async (postId: string) => {
    if (!user) return;
    try {
      await togglePostLike(postId, user.uid);
    } catch (error) {
      console.error('Error toggling like:', error);
      showNotification('Failed to update like', 'error');
    }
  };

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  })();

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-6 space-y-5">
        <div className="skeleton h-28 rounded-3xl" />
        <div className="skeleton h-24 rounded-3xl" />
        <div className="skeleton h-64 rounded-3xl" />
        <div className="skeleton h-64 rounded-3xl" />
      </div>
    );
  }

  return (
    <div className="min-h-full">
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 px-5 py-3.5 rounded-2xl shadow-elevated animate-slide-up ${
            notification.type === 'success' ? 'bg-accent-green' : 'bg-red-600'
          } text-white text-sm font-medium`}
        >
          {notification.message}
        </div>
      )}

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6 py-5 lg:py-7">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
          <section className="mx-auto w-full min-w-0 max-w-2xl space-y-5 xl:mx-0 xl:max-w-none">
            {/* Greeting */}
            <div className="px-1 animate-fade-in-up">
              <h1 className="text-xl font-bold tracking-tight text-text-primary sm:text-2xl">
                {greeting}
                {userProfile?.displayName ? `, ${userProfile.displayName.split(' ')[0]}` : ''} 👋
              </h1>
              <p className="mt-0.5 text-sm text-text-muted">Here's what your community is sharing today.</p>
            </div>

            {/* Composer */}
            <div className="rounded-3xl border border-dark-border/70 bg-dark-card p-4 shadow-soft animate-fade-in-up">
              <div className="flex items-center gap-3">
                {userProfile?.profileImageUrl ? (
                  <img
                    src={userProfile.profileImageUrl}
                    alt=""
                    className="h-11 w-11 rounded-full object-cover ring-2 ring-accent-blue/20"
                  />
                ) : (
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-blue/15">
                    <User className="h-5 w-5 text-accent-blue" />
                  </div>
                )}
                {user ? (
                  <button
                    type="button"
                    onClick={() => setShowComposer(true)}
                    className="flex-1 rounded-full bg-surface-sunken/80 px-5 py-3 text-left text-sm font-medium text-text-muted transition-all duration-200 ease-premium hover:bg-surface-sunken hover:text-text-secondary"
                  >
                    {`What's on your mind, ${userProfile?.displayName?.split(' ')[0] || user.displayName || 'friend'}?`}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="flex-1 rounded-full bg-surface-sunken/80 px-5 py-3 text-left text-sm font-medium text-text-muted transition-all duration-200 ease-premium hover:bg-surface-sunken hover:text-text-secondary"
                  >
                    Sign in to share what is on your mind.
                  </Link>
                )}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 border-t border-dark-border/60 pt-3">
                {quickPostActions.map((action) => {
                  const Icon = action.icon;
                  const buttonClass = 'flex items-center justify-center gap-2 rounded-xl px-2 py-2 text-sm font-semibold text-text-secondary transition-colors duration-200 hover:bg-surface-sunken';
                  if (!user || action.to) {
                    return (
                      <Link key={action.label} to={user ? (action.to as string) : '/login'} className={buttonClass}>
                        <Icon className={`h-5 w-5 ${action.color}`} />
                        {action.label}
                      </Link>
                    );
                  }
                  return (
                    <button key={action.label} type="button" onClick={() => setShowComposer(true)} className={buttonClass}>
                      <Icon className={`h-5 w-5 ${action.color}`} />
                      {action.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* New-member welcome card (first two weeks, dismissible) */}
            {user && isNewMember && !welcomeCardDismissed && (
              <div className="aurora-bg relative overflow-hidden rounded-3xl border border-accent-green/30 bg-dark-card p-6 shadow-soft animate-fade-in-up">
                <button
                  type="button"
                  onClick={dismissWelcomeCard}
                  aria-label="Dismiss welcome card"
                  className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full text-text-muted transition-colors hover:bg-surface-sunken hover:text-text-primary"
                >
                  <X className="h-4 w-4" />
                </button>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent-green">
                  Welcome to the circle
                </p>
                <h2 className="mt-2 text-lg font-bold text-text-primary">
                  A few words before you dive in{userProfile?.displayName ? `, ${userProfile.displayName.split(' ')[0]}` : ''}.
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-text-secondary">
                  You'll find real stories here — including hard truths. We don't look away from them,
                  and we don't drown in them: every wound named in this circle is an invitation to
                  help heal it. Say hello whenever you're ready.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to="/welcome"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-gradient px-4 py-2 text-xs font-semibold text-white shadow-soft transition-all duration-200 ease-premium hover:-translate-y-0.5"
                  >
                    Your first steps
                  </Link>
                  <Link
                    to="/agreements"
                    className="inline-flex items-center gap-1.5 rounded-full border border-dark-border px-4 py-2 text-xs font-semibold text-text-secondary transition-colors hover:border-accent-blue/50 hover:text-text-primary"
                  >
                    Our agreements
                  </Link>
                  <Link
                    to="/weekly-meet"
                    className="inline-flex items-center gap-1.5 rounded-full border border-dark-border px-4 py-2 text-xs font-semibold text-text-secondary transition-colors hover:border-accent-blue/50 hover:text-text-primary"
                  >
                    This week's meeting
                  </Link>
                </div>
              </div>
            )}

            {/* Feed tabs */}
            <div className="rounded-3xl border border-dark-border/70 bg-dark-card p-1.5 shadow-soft animate-fade-in-up">
              <div className="grid grid-cols-4 gap-1" role="tablist" aria-label="Feed filters">
                {FEED_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative rounded-2xl px-2 py-2.5 text-sm font-semibold transition-all duration-200 ease-premium ${
                      activeTab === tab.id
                        ? 'bg-accent-blue/10 text-accent-blue'
                        : 'text-text-muted hover:bg-surface-sunken hover:text-text-primary'
                    }`}
                  >
                    {tab.label}
                    {activeTab === tab.id && (
                      <span className="absolute inset-x-6 -bottom-0.5 h-0.5 rounded-full bg-accent-blue" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Empty state */}
            {posts.length === 0 && (
              <div className="aurora-bg overflow-hidden rounded-3xl border border-dark-border/70 bg-dark-card p-6 shadow-soft animate-fade-in-up">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-accent-blue/15">
                    <MessageCircle className="h-5 w-5 text-accent-blue" />
                  </div>
                  <div>
                    <p className="font-semibold text-text-primary">The Kinship Way</p>
                    <p className="text-xs text-text-muted">Pinned welcome post</p>
                  </div>
                </div>
                <h2 className="text-xl font-bold text-text-primary">Your community feed is ready.</h2>
                <p className="mt-2 text-sm leading-6 text-text-secondary">
                  This is where members will share posts, photos, teachings, event updates, and everyday
                  conversation. Once people start posting, their updates will appear here in real time.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {user ? (
                    <button
                      type="button"
                      onClick={() => setShowComposer(true)}
                      className="inline-flex items-center gap-2 rounded-full bg-brand-gradient px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition-all duration-200 ease-premium hover:-translate-y-0.5 hover:shadow-glow-blue"
                    >
                      <PlusCircle className="h-4 w-4" />
                      Create Post
                    </button>
                  ) : (
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-2 rounded-full bg-brand-gradient px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition-all duration-200 ease-premium hover:-translate-y-0.5 hover:shadow-glow-blue"
                    >
                      <PlusCircle className="h-4 w-4" />
                      Create Post
                    </Link>
                  )}
                  <Link
                    to="/community-members"
                    className="inline-flex items-center gap-2 rounded-full border border-dark-border bg-dark-card px-5 py-2.5 text-sm font-semibold text-text-primary transition-all duration-200 ease-premium hover:border-accent-blue/50 hover:-translate-y-0.5"
                  >
                    <Users className="h-4 w-4" />
                    Find People
                  </Link>
                </div>
              </div>
            )}

            {/* Tab-specific empty state */}
            {posts.length > 0 && visiblePosts.length === 0 && (
              <div className="rounded-3xl border border-dark-border/70 bg-dark-card p-8 text-center shadow-soft animate-fade-in-up">
                <MessageCircle className="mx-auto mb-3 h-8 w-8 text-text-muted opacity-60" />
                <p className="text-sm text-text-secondary">{TAB_EMPTY_COPY[activeTab]}</p>
              </div>
            )}

            {/* Posts */}
            {visiblePosts.length > 0 && (
              <div className="space-y-5">
                {renderedPosts.map((post, index) => (
                  <article
                    key={post.id}
                    className="cursor-pointer rounded-3xl border border-dark-border/70 bg-dark-card p-5 shadow-soft transition-all duration-300 ease-premium hover:-translate-y-0.5 hover:border-accent-blue/40 hover:shadow-elevated animate-fade-in-up"
                    style={{ animationDelay: `${Math.min(index, 6) * 60}ms` }}
                    onClick={() => setSelectedPostForFeed(post)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setSelectedPostForFeed(post)}
                    aria-label={`Open post: ${post.title}`}
                  >
                    <div className="mb-4 flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                      <PostAuthorLink
                        authorId={post.authorId}
                        authorName={post.authorName}
                        authorHandle={post.authorHandle}
                        authorImageUrl={post.authorImageUrl}
                        timestamp={formatTimestamp(post.createdAt)}
                      />
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${categoryStyle[post.category] || 'bg-surface-sunken text-text-secondary'}`}>
                        {categoryLabel[post.category] || post.category}
                      </span>
                    </div>

                    <h2 className="mb-2 text-lg font-bold tracking-tight text-text-primary">{post.title}</h2>
                    <p className="mb-3 whitespace-pre-wrap text-[15px] leading-relaxed text-text-secondary line-clamp-4">{post.content}</p>

                    {post.kinshipGroups && post.kinshipGroups.length > 0 && (
                      <KinshipGroupChips groups={post.kinshipGroups} />
                    )}

                    {post.images && post.images.length > 0 && (
                      <div className="mb-4 overflow-hidden rounded-2xl">
                        <img
                          src={post.images[0]}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          className="max-h-72 w-full object-cover transition-transform duration-500 ease-premium hover:scale-[1.02]"
                        />
                      </div>
                    )}

                    {post.tags && post.tags.length > 0 && (
                      <div className="mb-4 flex flex-wrap gap-2">
                        {post.tags.slice(0, 4).map((tag, tagIndex) => (
                          <span key={tagIndex} className="rounded-full bg-accent-blue/10 px-2.5 py-1 text-xs font-medium text-accent-blue">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-2 border-t border-dark-border/60 pt-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleLike(post.id); }}
                        className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-all duration-200 ease-premium ${
                          user && (post.likedBy || []).includes(user.uid)
                            ? 'bg-rose-500/10 text-rose-500 hover:bg-rose-500/15'
                            : 'text-text-muted hover:bg-surface-sunken hover:text-rose-500'
                        }`}
                      >
                        <Heart className={`h-4 w-4 transition-transform duration-200 ease-spring active:scale-125 ${user && (post.likedBy || []).includes(user.uid) ? 'fill-current' : ''}`} />
                        {(post.likedBy && post.likedBy.length > 0 ? post.likedBy.length : post.likes) || 0}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setExpandedCommentsPostId((id) => (id === post.id ? null : post.id)); }}
                        className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-text-muted transition-colors duration-200 hover:bg-surface-sunken hover:text-accent-blue"
                      >
                        <MessageCircle className="h-4 w-4" />
                        {post.comments || 0} Comments
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleShare(post); }}
                        className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-text-muted transition-colors duration-200 hover:bg-surface-sunken hover:text-accent-blue"
                      >
                        <Share2 className="h-4 w-4" />
                        Share
                      </button>
                    </div>

                    {expandedCommentsPostId === post.id && (
                      <PostCommentsSection postId={post.id} currentUser={user} formatTimestamp={formatTimestamp} onNotification={showNotification} />
                    )}
                  </article>
                ))}

                {/* Infinite-scroll sentinel */}
                {visibleCount < visiblePosts.length && (
                  <div ref={loadMoreRef} className="space-y-5">
                    <div className="skeleton h-40 rounded-3xl" />
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Right rail — desktop only */}
          <RightRail posts={posts} className="hidden xl:block" />
        </div>
      </div>

      {/* Create post modal */}
      {showComposer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={closeComposer} role="dialog" aria-modal="true" aria-label="Create post">
          <div className="bg-dark-card border border-dark-border/70 rounded-3xl shadow-elevated w-full max-w-xl max-h-[90vh] overflow-y-auto animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between p-3.5 border-b border-dark-border/60 bg-dark-card/95 backdrop-blur rounded-t-3xl">
              <span className="text-sm font-semibold text-text-muted">Create Post</span>
              <button type="button" onClick={closeComposer} className="grid h-9 w-9 place-items-center rounded-full text-text-muted hover:text-text-primary hover:bg-surface-sunken transition-colors" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleComposerSubmit} className="p-5 space-y-4">
              <input
                type="text"
                value={composerData.title}
                onChange={(e) => setComposerData((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Title"
                maxLength={120}
                className="w-full rounded-xl border border-dark-border bg-surface-sunken/60 px-4 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-blue focus:outline-none"
              />
              <textarea
                value={composerData.content}
                onChange={(e) => setComposerData((prev) => ({ ...prev, content: e.target.value }))}
                placeholder={`What's on your mind, ${userProfile?.displayName?.split(' ')[0] || 'friend'}?`}
                rows={5}
                className="w-full resize-none rounded-xl border border-dark-border bg-surface-sunken/60 px-4 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-blue focus:outline-none"
              />
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-text-secondary transition-colors hover:bg-surface-sunken">
                  <Image className="h-5 w-5 text-accent-green" />
                  {composerUploading ? 'Uploading...' : 'Add Photo'}
                  <input type="file" accept="image/*" onChange={handleComposerImageUpload} disabled={composerUploading} className="hidden" />
                </label>
                <select
                  value={composerCategory}
                  onChange={(e) => setComposerCategory(e.target.value as Post['category'])}
                  className="rounded-xl border border-dark-border bg-surface-sunken/60 px-3 py-2 text-sm text-text-secondary focus:border-accent-blue focus:outline-none"
                  aria-label="Post category"
                >
                  {Object.entries(categoryLabel).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              {composerData.imageUrl && (
                <div className="relative">
                  <img src={composerData.imageUrl} alt="Upload preview" className="w-full max-h-64 rounded-2xl object-cover" />
                  <button
                    type="button"
                    onClick={() => setComposerData((prev) => ({ ...prev, imageUrl: '' }))}
                    className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                    aria-label="Remove image"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
              <div className="flex justify-end gap-2 border-t border-dark-border/60 pt-4">
                <button
                  type="button"
                  onClick={closeComposer}
                  className="rounded-full px-5 py-2.5 text-sm font-semibold text-text-secondary transition-colors hover:bg-surface-sunken"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={composerSubmitting || composerUploading || !composerData.title.trim() || !composerData.content.trim()}
                  className="rounded-full bg-brand-gradient px-6 py-2.5 text-sm font-semibold text-white shadow-soft transition-all duration-200 ease-premium hover:-translate-y-0.5 hover:shadow-glow-blue disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  {composerSubmitting ? 'Posting...' : 'Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Post detail modal */}
      {selectedPostForFeed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setSelectedPostForFeed(null)} role="dialog" aria-modal="true" aria-label="Post detail">
          <div className="bg-dark-card border border-dark-border/70 rounded-3xl shadow-elevated w-full max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between p-3.5 border-b border-dark-border/60 bg-dark-card/95 backdrop-blur rounded-t-3xl">
              <span className="text-sm font-semibold text-text-muted">Post</span>
              <button type="button" onClick={() => setSelectedPostForFeed(null)} className="grid h-9 w-9 place-items-center rounded-full text-text-muted hover:text-text-primary hover:bg-surface-sunken transition-colors" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 flex-1 overflow-y-auto">
              {(() => {
                const post = selectedPostForFeed;
                return (
                  <article className="p-0">
                    <div className="flex items-center justify-between mb-4 gap-3">
                      <PostAuthorLink
                        authorId={post.authorId}
                        authorName={post.authorName}
                        authorHandle={post.authorHandle}
                        authorImageUrl={post.authorImageUrl}
                        timestamp={formatTimestamp(post.createdAt)}
                        onNavigate={() => setSelectedPostForFeed(null)}
                      />
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${categoryStyle[post.category] || 'bg-surface-sunken text-text-secondary'}`}>
                          {categoryLabel[post.category] || post.category}
                        </span>
                        {post.authorId && (
                          <Link
                            to={getProfilePath(post.authorId, post.authorHandle)}
                            onClick={() => setSelectedPostForFeed(null)}
                            className="text-xs text-accent-blue hover:underline"
                          >
                            View profile
                          </Link>
                        )}
                      </div>
                    </div>
                    <h2 className="text-lg font-bold tracking-tight text-text-primary mb-2">{post.title}</h2>
                    <p className="text-[15px] leading-relaxed text-text-secondary mb-3 whitespace-pre-wrap">{post.content}</p>
                    {post.kinshipGroups && post.kinshipGroups.length > 0 && (
                      <KinshipGroupChips groups={post.kinshipGroups} />
                    )}
                    {post.images && post.images.length > 0 && (
                      <div className="mb-4 space-y-2">
                        {post.images.map((imageUrl, index) => (
                          <img key={index} src={imageUrl} alt={`Post ${index + 1}`} loading="lazy" decoding="async" className="w-full max-h-96 object-cover rounded-2xl" />
                        ))}
                      </div>
                    )}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.map((tag, index) => (
                          <span key={index} className="rounded-full bg-accent-blue/10 px-2.5 py-1 text-xs font-medium text-accent-blue">#{tag}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-3 border-t border-dark-border/60">
                      <button
                        type="button"
                        onClick={() => handleLike(post.id)}
                        className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-all duration-200 ease-premium ${
                          user && (post.likedBy || []).includes(user.uid)
                            ? 'bg-rose-500/10 text-rose-500 hover:bg-rose-500/15'
                            : 'text-text-muted hover:bg-surface-sunken hover:text-rose-500'
                        }`}
                      >
                        <Heart className={`h-4 w-4 ${user && (post.likedBy || []).includes(user.uid) ? 'fill-current' : ''}`} />
                        {(post.likedBy && post.likedBy.length > 0 ? post.likedBy.length : post.likes) || 0}
                      </button>
                      <span className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-text-muted">
                        <MessageCircle className="h-4 w-4" />
                        {post.comments || 0} comments
                      </span>
                    </div>
                    <PostCommentsSection postId={post.id} currentUser={user} formatTimestamp={formatTimestamp} onNotification={showNotification} />
                  </article>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feed;
